import { Option } from "model";

/**
 * Categories accepted by the backend for system templates. Mirrors the marketplace's
 * `EmailTemplateCategory` union. Hardcoded here (not loaded from master data) because
 * the set is small + stable; if it grows, update this constant alongside the BE.
 */
export type EmailTemplateCategory =
  | "promotional"
  | "seasonal"
  | "engagement"
  | "system_default";

/**
 * Options consumed by the editor's category picker (admin's `<SelectField>` expects
 * `Option<string>`). Display labels are user-facing — keep them in title case.
 */
export const EMAIL_TEMPLATE_CATEGORIES: Array<Option<EmailTemplateCategory>> = [
  { id: "promotional",    name: "Promotional" },
  { id: "seasonal",       name: "Seasonal" },
  { id: "engagement",     name: "Engagement" },
  { id: "system_default", name: "System Default" }
];

export const DEFAULT_CATEGORY: EmailTemplateCategory = "engagement";

/**
 * A single placeholder offered by Unlayer's merge-tags panel. Matches the backend's
 * `EmailEditorMergeTags::DESCRIPTORS` shape exactly.
 */
export interface MergeTag {
  name: string;
  value: string;
}

/**
 * Unlayer bootstrap config fetched from `GET admin/email_editor/config`. The values
 * feed `react-email-editor`'s `projectId` + `options.user` + `options.mergeTags`.
 *
 * `projectId` may be `null` when the deploy hasn't set the Unlayer credentials —
 * the editor renders an error box in that case instead of attempting to mount.
 */
export interface EmailEditorConfig {
  projectId: number | null;
  /** Super admin's id — what Unlayer keys per-user assets/templates by. */
  userId: number;
  /** HMAC of `userId` using the Unlayer hmac_secret; `null` when secret is unset. */
  signature: string | null;
  mergeTags: Array<MergeTag>;
}

/**
 * System email template as authored from the admin app. Mirrors the backend's
 * serialized shape (`email_template_json` in `super_admins_controller.rb`).
 *
 * `html` / `designJson` / `createdAt` are only populated by the show endpoint
 * (`full: true`); the index endpoint returns the lightweight subset.
 *
 * `subjectDefault` is preserved as optional for forward-compat with templates
 * that already carry one in production. The admin authoring flow does NOT set
 * it (product decision — campaigns derive their subject elsewhere).
 */
export interface SystemEmailTemplate {
  id: number;
  uuid: string;
  name: string;
  category: EmailTemplateCategory;
  subjectDefault?: string | null;
  system: boolean;
  published: boolean;
  updatedAt: string;
  // Full-record fields (only present after a show call)
  html?: string;
  designJson?: object | null;
  marketplaceId?: number | null;
  createdAt?: string;
}

/**
 * What we send to BE on create/update. We never send `subject_default` from
 * admin — the BE permits the param but the authoring flow leaves it null.
 */
export interface EmailTemplateSavePayload {
  name: string;
  category: EmailTemplateCategory;
  html: string;
  designJson: object;
  published: boolean;
}
